// API client for backend communication

const API_BASE = '/api';

interface FetchOptions extends RequestInit {
  params?: Record<string, string | number | boolean>;
}

function getAuthHeaders(): Record<string, string> {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function fetchApi<T>(endpoint: string, options: FetchOptions = {}): Promise<T> {
  const { params, ...fetchOptions } = options;
  
  let url = `${API_BASE}${endpoint}`;
  
  if (params) {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, String(value));
      }
    });
    const queryString = searchParams.toString();
    if (queryString) {
      url += `?${queryString}`;
    }
  }
  
  const response = await fetch(url, {
    ...fetchOptions,
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
      ...fetchOptions.headers,
    },
  });
  
  if (!response.ok) {
    // Auto-logout on expired/invalid token
    if (response.status === 401) {
      localStorage.removeItem('token');
      const isAdmin = window.location.hostname.split('.')[0] === 'admin';
      window.location.href = isAdmin ? '/admin/login' : '/login';
      throw new Error('Session expired. Please log in again.');
    }
    const error = await response.json().catch(() => ({ detail: 'Request failed' }));
    throw new Error(error.detail || `HTTP error! status: ${response.status}`);
  }
  
  return response.json();
}

// Dashboard
export const getDashboardStats = () => fetchApi('/dashboard/stats');
export const getRecentActivity = (limit = 10) => 
  fetchApi('/dashboard/recent-activity', { params: { limit } });
export const getActivityClipUrl = async (eventId: number): Promise<string> => {
  const result = await fetchApi<{ url: string }>(`/dashboard/recent-activity/${eventId}/clip-ticket`);
  return result.url;
};

// Buildings
export const getBuildings = (params?: { skip?: number; limit?: number; search?: string }) =>
  fetchApi('/buildings', { params });
export const getBuilding = (id: number) => fetchApi(`/buildings/${id}`);
export const createBuilding = (data: Record<string, unknown>) =>
  fetchApi('/buildings', { method: 'POST', body: JSON.stringify(data) });
export const updateBuilding = (id: number, data: Record<string, unknown>) =>
  fetchApi(`/buildings/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteBuilding = (id: number) =>
  fetchApi(`/buildings/${id}`, { method: 'DELETE' });

// Units
export const getUnits = (params?: { building_id?: number; skip?: number; limit?: number }) =>
  fetchApi('/units', { params });
export const createUnit = (data: Record<string, unknown>) =>
  fetchApi('/units', { method: 'POST', body: JSON.stringify(data) });

// Tenants
export const generatePin = (): Promise<{ pin_code: string }> =>
  fetchApi('/tenants/generate-pin');
export const getTenants = (params?: { 
  skip?: number; 
  limit?: number; 
  status?: string; 
  search?: string;
  building_id?: number;
  sync_filter?: string;
  registration_filter?: string;
  review_filter?: string;
}) => fetchApi('/tenants', { params });
export const getTenant = (id: number) => fetchApi(`/tenants/${id}`);
export const createTenant = (data: Record<string, unknown>) =>
  fetchApi('/tenants', { method: 'POST', body: JSON.stringify(data) });
export const updateTenant = (id: number, data: Record<string, unknown>) =>
  fetchApi(`/tenants/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteTenant = (id: number) =>
  fetchApi(`/tenants/${id}`, { method: 'DELETE' });
export const deactivateUnifiTenant = (id: number) =>
  fetchApi(`/tenants/${id}/deactivate-unifi`, { method: 'POST' });
export const moveInTenant = (id: number, data: Record<string, unknown>) =>
  fetchApi(`/tenants/${id}/move-in`, { method: 'POST', body: JSON.stringify(data) });
export const moveOutTenant = (id: number, data: Record<string, unknown>) =>
  fetchApi(`/tenants/${id}/move-out`, { method: 'POST', body: JSON.stringify(data) });

// Vehicles
export const getVehicles = (params?: { 
  skip?: number; 
  limit?: number; 
  tenant_id?: number;
  search?: string;
}) => fetchApi('/vehicles', { params });
export const getVehicle = (id: number) => fetchApi(`/vehicles/${id}`);
export const createVehicle = (data: Record<string, unknown>) =>
  fetchApi('/vehicles', { method: 'POST', body: JSON.stringify(data) });
export const updateVehicle = (id: number, data: Record<string, unknown>) =>
  fetchApi(`/vehicles/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteVehicle = (id: number) =>
  fetchApi(`/vehicles/${id}`, { method: 'DELETE' });
export const lookupPlate = (plate: string) =>
  fetchApi(`/vehicles/lookup/${encodeURIComponent(plate)}`);

// Access Events
export const getAccessEvents = (params?: {
  skip?: number;
  limit?: number;
  start_date?: string;
  end_date?: string;
  event_type?: string;
  tenant_id?: number;
  door_id?: number;
}) => fetchApi('/access-events', { params });
export const getRecentEvents = (hours = 24, limit = 100) =>
  fetchApi('/access-events/recent', { params: { hours, limit } });
export const getDeniedEvents = (hours = 24, limit = 50) =>
  fetchApi('/access-events/denied', { params: { hours, limit } });
export const getLprEvents = (hours = 24, matchedOnly = false) =>
  fetchApi('/access-events/lpr', { params: { hours, matched_only: matchedOnly } });

// Resident Self-Registration (public - no auth)
export const residentSelfRegister = (data: {
  first_name: string; last_name: string; email: string; password: string;
  building_name: string; unit_number: string; room_label: string;
}) => fetchApi<{ success: boolean; pin_code: string; message: string }>(
  '/auth/resident/register', { method: 'POST', body: JSON.stringify(data) }
);

// Admin - Password Reset
export const adminResetPassword = (email: string, newPassword: string) =>
  fetchApi('/auth/admin/reset-password', {
    method: 'POST',
    body: JSON.stringify({ user_email: email, new_password: newPassword }),
  });

// Admin - User Management
export const getAdminUsers = () =>
  fetchApi<AdminUser[]>('/auth/admin/users');

export const createAdminUser = (data: { email: string; first_name: string; last_name: string; password: string; role: string }) =>
  fetchApi('/auth/admin/users', { method: 'POST', body: JSON.stringify(data) });

export const updateAdminUser = (id: number, data: { first_name?: string; last_name?: string; role?: string; is_active?: boolean }) =>
  fetchApi(`/auth/admin/users/${id}`, { method: 'PUT', body: JSON.stringify(data) });

export const deleteAdminUser = (id: number) =>
  fetchApi(`/auth/admin/users/${id}`, { method: 'DELETE' });

export interface AdminUser {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  is_active: boolean;
  last_login: string | null;
  created_at: string | null;
}

// Analytics
export const getAnalyticsSummary = (days = 30) =>
  fetchApi<AnalyticsSummary>('/analytics/summary', { params: { days } });
export const getFirstTimeErrors = (days = 30) =>
  fetchApi<FirstTimeErrorsReport>('/analytics/first-time-errors', { params: { days } });

export interface AnalyticsSummary {
  period_days: number;
  total_page_views: number;
  unique_sessions: number;
  unique_ips: number;
  today: { page_views: number; unique_visitors: number };
  funnel: {
    login_page_views: number;
    first_time_setup_started: number;
    first_time_setup_completed: number;
    returning_login_started: number;
    returning_login_completed: number;
    self_registration_started: number;
    self_registration_completed: number;
  };
  daily: { date: string; views: number; unique_visitors: number }[];
  top_referrers: { referrer: string; count: number }[];
}

export interface FirstTimeErrorsReport {
  period_days: number;
  summary: {
    first_time_started: number;
    first_time_completed: number;
    first_time_failed: number;
    unique_failed_users: number;
  };
  users: {
    email: string;
    failure_count: number;
    last_error_reason: string;
    last_failure_at: string | null;
    completed_after_failure: boolean;
  }[];
}

// Sync
export const getSyncStatus = () => fetchApi('/sync/status');
export const syncTenant = (id: number) =>
  fetchApi(`/sync/tenant/${id}`, { method: 'POST' });
export const syncAllTenants = () =>
  fetchApi('/sync/all', { method: 'POST' });

// Excel Import
export const importExcel = async (file: File, dryRun = false) => {
  const formData = new FormData();
  formData.append('file', file);
  
  const url = `/api/import/excel${dryRun ? '?dry_run=true' : ''}`;
  const response = await fetch(url, {
    method: 'POST',
    body: formData,
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Import failed' }));
    throw new Error(error.detail);
  }
  
  return response.json();
};

export default {
  getDashboardStats,
  getRecentActivity,
  getBuildings,
  getTenants,
  getVehicles,
  getAccessEvents,
};






